<template>
  <v-expansion-panels class="template-form">
    <v-expansion-panel>
      <v-expansion-panel-header>
        <template v-slot:actions>
          <v-icon class="header-icon mr-2">$expand</v-icon>
        </template>
        <strong class="header-title">{{ title }} selected</strong>
      </v-expansion-panel-header>
      <v-expansion-panel-content>
        <v-list-item
          class="align-start flex-column"
          v-for="(template, index) in templates"
          :key="index"
        >
          <div class="d-flex flex-start">
            <inline-svg
              class="mr-2"
              height="20"
              width="20"
              :src="template.icon"
            />
            <span>{{ template.name }}</span>
          </div>
          <div
            class="ml-8 d-flex flex-column mb-2"
            v-if="type === 'publishingSchedule'"
          >
            <span class="mt-1" v-for="tier in template.tiers" :key="tier.id">
              {{ tier.tier_name }}
            </span>
          </div>
        </v-list-item>
      </v-expansion-panel-content>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<script>
import { createNamespacedHelpers } from "vuex";
import { ORGANISATION_MESSAGE_TEMPLATES_NAMESPACE } from "@/store/modules/organisations/organisation-message-templates";
import { FETCH_ORGANISATION_MESSAGE_TEMPLATES } from "@/store/modules/organisations/organisation-message-templates/actions";
import { copyState } from "@/utils/helpers";
import { map, size } from "lodash";
import InlineSvg from "vue-inline-svg";
import { ORGANISATION_APPLICATION_QUESTION_TEMPLATES_NAMESPACE } from "@/store/modules/organisations/organisation-application-question-templates";
import { FETCH_APPLICATION_QUESTION_TEMPLATES } from "@/store/modules/organisations/organisation-application-question-templates/actions";
import { ORGANISATION_INTERVIEW_FORMAT_TEMPLATES_NAMESPACE } from "@/store/modules/organisations/organisation-interview-format-templates";
import { FETCH_ORGANISATION_INTERVIEW_FORMAT_TEMPLATES } from "@/store/modules/organisations/organisation-interview-format-templates/actions";
import { FETCH_PUBLISHING_SCHEDULE_TEMPLATES } from "@/store/modules/organisations/organisation-schedules/actions";
import { ORGANISATION_SCHEDULES_NAMESPACE } from "@/store/modules/organisations/organisation-schedules";
import pluralize from "pluralize";

const {
  mapActions: messageMapActions,
  mapState: messageMapState
} = createNamespacedHelpers(ORGANISATION_MESSAGE_TEMPLATES_NAMESPACE);
const {
  mapActions: applicationQuestionMapActions,
  mapState: applicationQuestionMapState
} = createNamespacedHelpers(
  ORGANISATION_APPLICATION_QUESTION_TEMPLATES_NAMESPACE
);
const {
  mapActions: interviewFormatMapActions,
  mapState: interviewFormatMapState
} = createNamespacedHelpers(ORGANISATION_INTERVIEW_FORMAT_TEMPLATES_NAMESPACE);
const {
  mapActions: publishingScheduleMapActions,
  mapState: publishingScheduleMapState
} = createNamespacedHelpers(ORGANISATION_SCHEDULES_NAMESPACE);

export default {
  props: {
    type: { type: String, required: true },
    tags: { type: Array, required: true }
  },
  components: { InlineSvg },
  data() {
    return {
      templates: []
    };
  },
  created() {
    if (size(this.tags)) {
      this.setSelectedTemplates(this.tags);
    }
  },
  computed: {
    ...messageMapState(copyState(["organisationMessageTemplates"])),
    ...applicationQuestionMapState(
      copyState(["organisationApplicationQuestionTemplates"])
    ),
    ...interviewFormatMapState(
      copyState(["organisationInterviewFormatTemplates"])
    ),
    ...publishingScheduleMapState(copyState(["publishingScheduleTemplates"])),
    title() {
      const count = size(this.templates);
      switch (this.type) {
        case "message":
          return `${count} message ${pluralize("template", count)}`;
        case "applicationQuestion":
          return `${count} application ${pluralize("question", count)}`;
        case "interviewFormat":
          return `${count} interview ${pluralize("template", count)}`;
        case "publishingSchedule":
          return `${count} publishing ${pluralize("schedule", count)}`;
        default:
          return `${count}`;
      }
    }
  },
  methods: {
    ...messageMapActions({
      fetchMessageTemplates: FETCH_ORGANISATION_MESSAGE_TEMPLATES
    }),
    ...applicationQuestionMapActions({
      fetchApplicationQuestionTemplates: FETCH_APPLICATION_QUESTION_TEMPLATES
    }),
    ...interviewFormatMapActions({
      fetchInterviewFormatTemplates: FETCH_ORGANISATION_INTERVIEW_FORMAT_TEMPLATES
    }),
    ...publishingScheduleMapActions({
      fetchPublishingScheduleTemplates: FETCH_PUBLISHING_SCHEDULE_TEMPLATES
    }),
    async setSelectedTemplates(tags) {
      const filter = { tags };
      switch (this.type) {
        case "message":
          await this.fetchMessageTemplates({ filter });
          this.templates = map(this.organisationMessageTemplates, template => {
            return {
              icon: require("@/assets/svg/navigation-drawer/messages_blue.svg"),
              name: template.subject
            };
          });
          break;
        case "applicationQuestion":
          await this.fetchApplicationQuestionTemplates({ filter });
          this.templates = map(
            this.organisationApplicationQuestionTemplates,
            template => {
              return {
                icon: require("@/assets/svg/application_question_icon.svg"),
                name: template.question
              };
            }
          );
          break;
        case "interviewFormat":
          await this.fetchInterviewFormatTemplates({ filter });
          this.templates = map(
            this.organisationInterviewFormatTemplates,
            template => {
              return {
                icon: require("@/assets/svg/navigation-drawer/interviews_blue.svg"),
                name: template.interviewName
              };
            }
          );
          break;
        case "publishingSchedule":
          await this.fetchPublishingScheduleTemplates({ filter });
          this.templates = map(this.publishingScheduleTemplates, template => {
            return {
              icon: require("@/assets/svg/publishing_schedule_icon.svg"),
              name: template.name,
              tiers: template.tiers
            };
          });
          break;
      }
    }
  },
  watch: {
    tags(newValue) {
      this.setSelectedTemplates(newValue);
    }
  }
};
</script>

<style lang="scss" scope>
.template-form.v-expansion-panels {
  margin-top: -20px;

  .v-list-item svg path,
  rect {
    fill: rgba(0, 0, 0, 0.87) !important;
  }

  line,
  circle {
    stroke: rgba(0, 0, 0, 0.87) !important;
  }

  .v-list-item {
    min-height: 34px;
    padding: 0 34px;
  }

  .v-expansion-panel--active > .v-expansion-panel-header {
    min-height: 48px;
  }

  span,
  strong {
    font-size: 16px;
  }
}

.header-icon {
  order: 0;
}

.header-title {
  order: 1;
}
</style>
